import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useSave } from "@ncp/ui";
import type { ChangeSet } from "../releases/api";
import type { DefinitionStatus } from "../forms/api";

/** Versioned definitions served by the shared definition routes (`/api/platform/{resource}`). */
export type GenericResource = "record-types" | "reports" | "screens" | "tools" | "rules" | "services";

/** The version metadata the server returns with every definition body. */
export interface DefinitionMeta {
  id: string;
  key: string;
  version: number;
  status: DefinitionStatus;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
  sourcePackageKey?: string | null;
}

export type Definition<B> = B & DefinitionMeta;

/** The common editable header of every definition body. */
export interface BaseBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
}

/** One catalogue row: the published and draft versions of a key folded together. */
export interface DefinitionSummary {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  tags: string[];
  publishedVersion?: number | null;
  publishedId?: string | null;
  publishedAt?: string | null;
  draftVersion?: number | null;
  draftId?: string | null;
  draftStatus?: DefinitionStatus | null;
  latestVersion: number;
  versionCount: number;
  updatedAt: string;
  updatedByName?: string | null;
  sourcePackageKey?: string | null;
}

const META_KEYS = ["id", "key", "version", "status", "changeNote", "createdByName", "createdAt", "updatedAt", "publishedAt", "publishedByName", "publishedByChangeSetId", "sourcePackageKey"] as const;

/** The editable body of a definition: the payload without its version metadata. */
export function bodyOf<B>(d: Definition<B>): B {
  const copy = { ...d } as Record<string, unknown>;
  for (const k of META_KEYS) delete copy[k];
  return copy as B;
}

const base = (resource: GenericResource) => `/api/platform/${resource}`;

export function useDefinitionList<S extends DefinitionSummary = DefinitionSummary>(resource: GenericResource, params: { q?: string; status?: string; tag?: string } = {}, enabled = true) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.tag) qs.set("tag", params.tag);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({ queryKey: ["platform", "definitions", resource, "list", params], queryFn: () => api.get<S[]>(`${base(resource)}${suffix}`), staleTime: 10_000, enabled });
}

export function useDefinition<B>(resource: GenericResource, key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform", "definitions", resource, "one", key, version ?? "latest"],
    queryFn: () => api.get<Definition<B>>(`${base(resource)}/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
  });
}

export function useDefinitionActions<B>(resource: GenericResource) {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["platform", "definitions", resource] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: B; changeNote?: string }) => api.post<Definition<B>>(base(resource), input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: B; changeNote?: string }) => api.put<Definition<B>>(`${base(resource)}/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<Definition<B>>(`${base(resource)}/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`${base(resource)}/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`${base(resource)}/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<Definition<B>>(`${base(resource)}/${id}/withdraw`), onSuccess: invalidate }),
  };
}

/** A key made from a label: lowercase words joined by hyphens. */
export function keyFromLabel(label: string): string {
  return label.trim().toLowerCase().normalize("NFKD").replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 64);
}

/** A field key made from a label: camelCase ASCII. */
export function fieldKeyFromLabel(label: string): string {
  const words = label.trim().normalize("NFKD").replace(/[^\w\s]/g, " ").split(/\s+/).filter(Boolean);
  const key = words.map((w, i) => (i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1).toLowerCase())).join("").replace(/^[^a-z]+/, "");
  return key.slice(0, 64);
}
